import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { OnscreenKeyboardKey } from "@-/dev.improve.haniverse.components.ui.onscreen-keyboard-key";
import { styled } from "styled-components/native";
import { useCallback, useEffect, useState } from "react";
import {
  View,
  type LayoutChangeEvent,
  type NativeSyntheticEvent,
  type TextLayoutEventData,
} from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import arrayLength from "@-/com.npmjs.extra-array__length";

// Theme colors mirrored from apps/haniverse-expo/global.css. Kept in sync manually because
// this package can't depend on uniwind (workspace/install constraints) and styled-components
// `var(--color-X)` doesn't resolve our uniwind-managed theme tokens on native — there's no
// ancestor styled-component declaring them. Edit both columns if global.css changes.
const COLOR_BY_THEME = {
  dark: {
    foreground: "hsl(0, 0%, 98%)",
    mutedForeground: "hsl(0, 0%, 63.9%)",
    primary: "hsl(0, 0%, 98%)",
  },
  light: {
    foreground: "hsl(0, 0%, 3.9%)",
    mutedForeground: "hsl(0, 0%, 45.1%)",
    primary: "hsl(0, 0%, 9%)",
  },
} as const;

// Design ratios — every text size in a key is a percentage of the key's own inline-size.
// `cqi` resolves at render time against the nearest `container-type: inline-size` ancestor
// (the `KeyShell` below), so the entire key visually scales identically at any keyWidth.
const QWERTY_LETTER_RATIO_CQI = 38;
const PINYIN_HINT_RATIO_CQI = 31;
const TONE_MARK_RATIO_CQI = 62;
const TONE_MARK_TOP_PADDING_CQI = 14;
const TONE_MARK_TOP_PADDING_EXTRA_FOR_BOTTOM_ROW_CQI = 24;
const TONE_MARK_TOP_PADDING_FOR_TONE_3_CQI = 14;
const TONE_MARK_TOP_PADDING_EXTRA_FOR_TONE_3_PX = 1;
const TONE_MARK_RIGHT_PADDING_PX = 2;
const TONE_MARK_RIGHT_PADDING_FOR_TONE_1_PX = 4;
const ICON_SIZE_RATIO_CQI = 32;

/** Floor for the auto-shrink fallback so a layout race never collapses the hint to zero. */
const MIN_HINT_SHRINK_FACTOR = 0.5;

// Drop Android's extra font padding so the tightly-positioned key glyphs sit where the cqi math
// places them. Passed as a real boolean PROP (not a CSS `include-font-padding` declaration,
// which styled-components/native v7 mis-parses to a Double and crashes RCTText — see the styled
// `*.Text` definitions above). `includeFontPadding` is Android-only; iOS/web ignore it.
const ANDROID_TEXT_PADDING_PROP = { includeFontPadding: false } as const;

const KeyShell = styled(View)`
  position: relative;
  flex: 1;
  container-type: inline-size;
`;

// NOTE: do NOT set `include-font-padding: false` in these CSS template literals.
// styled-components/native v7 runs unknown CSS values through a numeric parser, so the boolean
// `false` reaches RCTText as a Double and crashes Android Fabric with "java.lang.Double cannot
// be cast to java.lang.Boolean" while preallocating the view (hit on the drill page's pinyin
// keyboard). Instead pass `includeFontPadding={false}` as a real boolean PROP at each render
// site (see ANDROID_TEXT_PADDING_PROP below) — on iOS/web it's a harmless no-op.
const QwertyLetter = styled.Text<{ $color: string }>`
  position: absolute;
  bottom: 1px;
  left: 1px;
  font-weight: 600;
  font-size: ${QWERTY_LETTER_RATIO_CQI}cqi;
  line-height: ${QWERTY_LETTER_RATIO_CQI}cqi;
  text-transform: uppercase;
  color: ${(p) => p.$color};
`;

const PinyinHint = styled.Text<{ $color: string; $shrinkFactor: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding-right: 1px;
  text-align: right;
  font-weight: 500;
  background-color: transparent;
  font-size: ${(p) => PINYIN_HINT_RATIO_CQI * p.$shrinkFactor}cqi;
  color: ${(p) => p.$color};
`;

const ToneMarkText = styled.Text<{
  $color: string;
  $paddingRightPx: number;
  $paddingTopCqi: number;
  $paddingTopPx: number;
}>`
  position: absolute;
  top: 0;
  right: 0;
  padding-top: ${(p) =>
    p.$paddingTopPx > 0
      ? `calc(${p.$paddingTopCqi}cqi + ${p.$paddingTopPx}px)`
      : `${p.$paddingTopCqi}cqi`};
  padding-right: ${(p) => p.$paddingRightPx}px;
  font-weight: 600;
  font-size: ${TONE_MARK_RATIO_CQI}cqi;
  line-height: ${TONE_MARK_RATIO_CQI}cqi;
  color: ${(p) => p.$color};
`;

const ToneDigitText = styled.Text<{ $color: string }>`
  font-weight: 600;
  font-size: ${QWERTY_LETTER_RATIO_CQI}cqi;
  line-height: ${QWERTY_LETTER_RATIO_CQI}cqi;
  color: ${(p) => p.$color};
`;

const ToneDigitLeft = styled(ToneDigitText)`
  position: absolute;
  bottom: 1px;
  left: 1px;
`;

const ToneDigitRight = styled(ToneDigitText)`
  position: absolute;
  bottom: 1px;
  right: 1px;
`;

const IconContainer = styled(View)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  align-items: center;
  justify-content: center;
`;

export type PinyinKeyboardKeyLetterProps = {
  accessibilityLabel: string;
  disabled: boolean;
  isPending: boolean;
  keyHeight: number;
  keyWidth: number;
  layout: "letter";
  onPress: () => void;
  pinyinLabelList: readonly string[];
  qwertyKey: string;
};

export type PinyinKeyboardKeyToneProps = {
  accessibilityLabel: string;
  /** Digit shown in the bottom-left corner (like the qwerty letter on letter keys). */
  digitLabel?: string;
  /** Digit shown in the bottom-right corner — used by the neutral tone (0 left, 5 right). */
  digitLabelRight?: string;
  /** Visual fade when the tone does not apply yet (key may still be enabled). */
  dimmed?: boolean;
  disabled: boolean;
  keyHeight: number;
  keyWidth: number;
  layout: "tone";
  onPress: () => void;
  toneMark: string;
  toneMarkPaddingRightPx?: number;
  toneMarkPaddingTopCqi?: number;
};

export type PinyinKeyboardKeyIconProps = {
  accessibilityLabel: string;
  disabled: boolean;
  iconName: IconName;
  keyHeight: number;
  keyWidth: number;
  layout: "icon";
  onPress: () => void;
};

export type PinyinKeyboardKeyProps =
  | PinyinKeyboardKeyLetterProps
  | PinyinKeyboardKeyToneProps
  | PinyinKeyboardKeyIconProps;

/**
 * Returns a shrink factor in [MIN_HINT_SHRINK_FACTOR, 1] that, when multiplied into the
 * hint's `cqi` ratio, brings the widest laid-out line back inside the available width.
 *
 * The hint's own `onLayout` provides the available width; `onTextLayout` provides the
 * widest line's natural width. When the natural width exceeds the available width, we
 * compute the exact ratio that would have fit and apply it as the shrink factor. Resets
 * to 1 on every `pinyinLabel` change so a previously-shrunken cell doesn't stay shrunk
 * when it later renders a shorter string.
 */
function useHintShrinkFactor(pinyinLabel: string) {
  const [shrinkFactor, setShrinkFactor] = useState(1);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [naturalWidth, setNaturalWidth] = useState(0);

  useEffect(() => {
    setShrinkFactor(1);
    setNaturalWidth(0);
  }, [pinyinLabel]);

  useEffect(() => {
    if (availableWidth <= 0 || naturalWidth <= 0 || naturalWidth <= availableWidth) {
      return;
    }
    const nextFactor = Math.max(MIN_HINT_SHRINK_FACTOR, availableWidth / naturalWidth);
    setShrinkFactor((prev) => (Math.abs(prev - nextFactor) < 0.01 ? prev : nextFactor));
  }, [availableWidth, naturalWidth]);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    setAvailableWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
  }, []);

  const onTextLayout = useCallback((event: NativeSyntheticEvent<TextLayoutEventData>) => {
    let widest = 0;
    for (const line of event.nativeEvent.lines) {
      if (line.width > widest) {
        widest = line.width;
      }
    }
    if (widest <= 0) {
      return;
    }
    setNaturalWidth((prev) => (Math.abs(prev - widest) < 0.5 ? prev : widest));
  }, []);

  return { onLayout, onTextLayout, shrinkFactor };
}

function useThemeColors() {
  const scheme = useTheme();
  return COLOR_BY_THEME[scheme === "dark" ? "dark" : "light"];
}

function IconKey(props: PinyinKeyboardKeyIconProps) {
  const { accessibilityLabel, disabled, iconName, keyHeight, keyWidth, onPress } = props;

  return (
    <OnscreenKeyboardKey
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="keyboardkey"
      accessibilityState={{ disabled }}
      disabled={disabled}
      height={keyHeight}
      onPress={onPress}
      width={keyWidth}
    >
      <KeyShell pointerEvents="none">
        <IconContainer>
          <Icon
            className="text-foreground"
            name={iconName}
            // Sized from the shorter side so a double-width key (backspace) keeps a key-sized glyph.
            size={(Math.min(keyWidth, keyHeight) * ICON_SIZE_RATIO_CQI) / 100}
          />
        </IconContainer>
      </KeyShell>
    </OnscreenKeyboardKey>
  );
}

function getToneMarkPadding(
  digitLabel: string | undefined,
  toneMarkPaddingRightPx: number | undefined,
  toneMarkPaddingTopCqi: number | undefined,
) {
  if (toneMarkPaddingRightPx !== undefined || toneMarkPaddingTopCqi !== undefined) {
    return {
      paddingRightPx: toneMarkPaddingRightPx ?? TONE_MARK_RIGHT_PADDING_PX,
      paddingTopCqi: toneMarkPaddingTopCqi ?? TONE_MARK_TOP_PADDING_CQI,
      paddingTopPx: 0,
    };
  }

  if (digitLabel === "1") {
    return {
      paddingRightPx: TONE_MARK_RIGHT_PADDING_FOR_TONE_1_PX,
      paddingTopCqi: TONE_MARK_TOP_PADDING_CQI,
      paddingTopPx: 0,
    };
  }

  if (digitLabel === "3") {
    return {
      paddingRightPx: TONE_MARK_RIGHT_PADDING_PX,
      paddingTopCqi: TONE_MARK_TOP_PADDING_FOR_TONE_3_CQI,
      paddingTopPx: TONE_MARK_TOP_PADDING_EXTRA_FOR_TONE_3_PX,
    };
  }

  if (digitLabel === "2" || digitLabel === "4") {
    return {
      paddingRightPx: TONE_MARK_RIGHT_PADDING_PX,
      paddingTopCqi: TONE_MARK_TOP_PADDING_EXTRA_FOR_BOTTOM_ROW_CQI,
      paddingTopPx: 0,
    };
  }

  return {
    paddingRightPx: TONE_MARK_RIGHT_PADDING_PX,
    paddingTopCqi: TONE_MARK_TOP_PADDING_CQI,
    paddingTopPx: 0,
  };
}

function ToneKey(props: PinyinKeyboardKeyToneProps) {
  const {
    accessibilityLabel,
    digitLabel,
    digitLabelRight,
    dimmed,
    disabled,
    keyHeight,
    keyWidth,
    onPress,
    toneMark,
    toneMarkPaddingRightPx,
    toneMarkPaddingTopCqi,
  } = props;
  const { foreground } = useThemeColors();
  const toneMarkPadding = getToneMarkPadding(
    digitLabel,
    toneMarkPaddingRightPx,
    toneMarkPaddingTopCqi,
  );

  return (
    <OnscreenKeyboardKey
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="keyboardkey"
      accessibilityState={{ disabled }}
      dimmed={dimmed}
      disabled={disabled}
      height={keyHeight}
      onPress={onPress}
      width={keyWidth}
    >
      <KeyShell pointerEvents="none">
        <ToneMarkText
          $color={foreground}
          $paddingRightPx={toneMarkPadding.paddingRightPx}
          $paddingTopCqi={toneMarkPadding.paddingTopCqi}
          $paddingTopPx={toneMarkPadding.paddingTopPx}
          {...ANDROID_TEXT_PADDING_PROP}
        >
          {toneMark}
        </ToneMarkText>
        {digitLabel === undefined ? null : (
          <ToneDigitLeft $color={foreground} {...ANDROID_TEXT_PADDING_PROP}>
            {digitLabel}
          </ToneDigitLeft>
        )}
        {digitLabelRight === undefined ? null : (
          <ToneDigitRight $color={foreground} {...ANDROID_TEXT_PADDING_PROP}>
            {digitLabelRight}
          </ToneDigitRight>
        )}
      </KeyShell>
    </OnscreenKeyboardKey>
  );
}

function LetterKey(props: PinyinKeyboardKeyLetterProps) {
  const {
    accessibilityLabel,
    disabled,
    isPending,
    keyHeight,
    keyWidth,
    onPress,
    pinyinLabelList,
    qwertyKey,
  } = props;
  const pinyinLabel = pinyinLabelList.join("\n");
  const { onLayout: onHintLayout, onTextLayout, shrinkFactor } = useHintShrinkFactor(pinyinLabel);
  const { foreground, mutedForeground, primary } = useThemeColors();

  const qwertyColor = isPending ? primary : foreground;
  const hintColor = isPending ? primary : mutedForeground;

  return (
    <OnscreenKeyboardKey
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="keyboardkey"
      accessibilityState={{ disabled, selected: isPending }}
      disabled={disabled}
      height={keyHeight}
      highlighted={isPending}
      onPress={onPress}
      width={keyWidth}
    >
      <KeyShell pointerEvents="none">
        <PinyinHint
          $color={hintColor}
          $shrinkFactor={shrinkFactor}
          ellipsizeMode="clip"
          numberOfLines={Math.max(1, arrayLength(pinyinLabelList))}
          onLayout={onHintLayout}
          onTextLayout={onTextLayout}
          {...ANDROID_TEXT_PADDING_PROP}
        >
          {pinyinLabel}
        </PinyinHint>
        <QwertyLetter $color={qwertyColor} {...ANDROID_TEXT_PADDING_PROP}>
          {qwertyKey}
        </QwertyLetter>
      </KeyShell>
    </OnscreenKeyboardKey>
  );
}

export function PinyinKeyboardKey(props: PinyinKeyboardKeyProps) {
  if (props.layout === "icon") {
    return <IconKey {...props} />;
  }
  if (props.layout === "tone") {
    return <ToneKey {...props} />;
  }
  return <LetterKey {...props} />;
}
